import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { logoutAuth } from '../api/auth'
import type { ApiAuthTokens, ApiAuthUser } from '../api/types'
import {
  clearAuthSession,
  displayAuthName,
  getRefreshToken,
  readAuthSession,
  sessionFromTokens,
  subscribeAuthSession,
  writeAuthSession,
  type StoredAuthSession,
} from '../helpers/auth'

type AuthContextValue = {
  user: ApiAuthUser | null
  displayName: string
  isAuthenticated: boolean
  applyTokens: (tokens: ApiAuthTokens) => void
  updateUser: (user: ApiAuthUser) => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredAuthSession | null>(() => readAuthSession())

  useLayoutEffect(() => subscribeAuthSession(setSession), [])

  const applyTokens = useCallback((tokens: ApiAuthTokens) => {
    const next = sessionFromTokens(tokens)
    writeAuthSession(next)
  }, [])

  const updateUser = useCallback((user: ApiAuthUser) => {
    const current = readAuthSession()
    if (!current) return
    writeAuthSession({ ...current, user })
  }, [])

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken()
    try {
      await logoutAuth(refreshToken ?? undefined)
    } catch {
      // Still clear local session if the API call fails (expired token, offline, etc.).
    }
    clearAuthSession()
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      displayName: displayAuthName(session?.user ?? null),
      isAuthenticated: Boolean(session?.accessToken && session.user),
      applyTokens,
      updateUser,
      logout,
    }),
    [session, applyTokens, updateUser, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return value
}
